// Helper functions for ConsumptionRevenueTab component

import { IContextualMenuItem } from "@fluentui/react";
import { getNextColor } from "@fluentui/react-charting";
import { IChartProps } from "@fluentui/react-charting/lib/components/SankeyChart/SankeyChart.types";
import { Dispatch } from "react";
import { ChartOption } from "../../../../SharedComponents/Interfaces/CustomCardWithMetrics.interfaces";
import { aggregateDataByKey } from "../../../../Utils/js-common";
import { FilterPillDispatchProps, ReducerType } from "../../../CustomerSearch/CustomSearch.interfaces";
import { IRevenueItem, OutputItem } from "../ConsumptionRevenue/ConsumptionRevenueTab.interfaces";

// Helper: Sum product revenue by month
export const sumProductRevenueByMonth = (data: IRevenueItem[]): OutputItem[] => {
  const grouped: { [product: string]: { [month: string]: number } } = {};
  data.forEach((item) => {
    const product = item.acrProductName;
    const month = item.endOfMonth;
    if (!grouped[product]) grouped[product] = {};
    grouped[product][month] = (grouped[product][month] || 0) + (item.revenue || 0);
  });
  return Object.entries(grouped).map(([name, monthMap]) => ({
    name,
    data: Object.entries(monthMap)
      .map(([month, y]) => ({ x: new Date(month), y }))
      .sort((a, b) => a.x.getTime() - b.x.getTime())
  }));
};

// Helper: Prepare overall revenue trend chart data
export const getOverallRevenueTrendChartData = (
  data: any[],
  handleDataPointClick: (option: ChartOption) => void
): IChartProps => {
  const option: ChartOption = {
    id: 1,
    field: "endOfMonth",
    key: "customer-revenue-trend-monthly-chart",
    title: "Overall Revenue Trend",
    type: "line",
    kustoLink: data?.map((d) => d.kustoLink)[0] || "",
    legendName: "Overall Revenue Trend"
  };
  const groupedData: any[] = aggregateDataByKey("endOfMonth", data, "revenue");
  const lineChartDataPoints = groupedData
    ?.map(({ name, value }) => ({
      x: new Date(new Date(name).toISOString()),
      y: value,
      onDataPointClick: () => handleDataPointClick({ ...option, value: name })
    }))
    .sort((a, b) => a.x.getTime() - b.x.getTime());

  return {
    lineChartData: [
      {
        legend: "Overall Revenue Trend",
        data: lineChartDataPoints,
        color: "#637cef",
        lineOptions: {
          lineBorderWidth: 4
        }
      }
    ]
  };
};

// Helper: Prepare product revenue trend chart data
export const getProductRevenueTrendChartData = (data: IRevenueItem[]): IChartProps => {
  const output: OutputItem[] = sumProductRevenueByMonth(data);
  return {
    lineChartData: output.map((item, index) => ({
      legend: item.name,
      data: item.data,
      color: getNextColor(index),
      lineOptions: {
        lineBorderWidth: 4
      }
    }))
  };
};

export const dispatchLoadMenuItems =
  (dispatch: Dispatch<FilterPillDispatchProps>) => (items?: IContextualMenuItem[]) => {
    dispatch({ type: ReducerType.LoadMenuItems, data: items || [] });
  };

export const dispatchMenuItemsUpdate = (dispatch: Dispatch<FilterPillDispatchProps>) => (items?: string[]) => {
  dispatch({ type: ReducerType.UpdateSelectedMenuItems, data: items || [] });
};
